import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Hello.css';
import Pagination from './Pagination';
import config from './config';

function Card({ title, children }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function Table({ data, setSort, columns, sortColumn, sortDirection, loading }) {
  const navigate = useNavigate();

  const handleSessionClick = (session) => {
    navigate(`/session/${session.id}`, { state: { session } });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const reorderedColumns = [
    ...columns.filter((col) => col === 'id'),
    ...columns.filter((col) => col === 'display_name' || col === 'email'),
    ...columns.filter((col) => col !== 'display_name' && col !== 'email' && !col.endsWith('id')),
    ...columns.filter((col) => col.endsWith('_id')),
  ];

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            {reorderedColumns.map((column) => (
              <th key={column}>
                {column}
                <button
                  className={`sort-button ${sortColumn === column && sortDirection === 'asc' ? 'active' : ''}`}
                  onClick={() => setSort([column, 'asc'])}
                >
                  ↑
                </button>
                <button
                  className={`sort-button ${sortColumn === column && sortDirection === 'desc' ? 'active' : ''}`}
                  onClick={() => setSort([column, 'desc'])}
                >
                  ↓
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((row, index) => (
            <tr key={index}>
              {reorderedColumns.map((column) => (
                <td key={column} className="table-cell">
                  {column === 'id' ? (
                    <button onClick={() => handleSessionClick(row)}>{row[column]}</button>
                  ) : column.includes('time') ? (
                    formatTimestamp(row[column])
                  ) : (
                    row[column]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tabs({ children, actions }) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0);

  return (
    <div>
      <div className="tabs-header">
        <div className="tabs">
          {children.map((child, index) => (
            <button
              key={index}
              className={activeTab === index ? 'active' : ''}
              onClick={() => setActiveTab(index)}
            >
              {child.props.label}
            </button>
          ))}
        </div>
        {actions && <div className="tabs-actions">{actions}</div>}
      </div>
      <div className="tab-content">{children[activeTab]}</div>
    </div>
  );
}

function Tab({ children }) {
  return <div>{children}</div>;
}

function Hello() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userMetrics, setUserMetrics] = useState(null);
  const [userSessions, setUserSessions] = useState(null);
  const [metricsPage, setMetricsPage] = useState(location.state?.metricsPage || 1);
  const [metricsPageSize, setMetricsPageSize] = useState(location.state?.metricsPageSize || 20);
  const [metricsTotalPages, setMetricsTotalPages] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(location.state?.sessionsPage || 1);
  const [sessionsPageSize, setSessionsPageSize] = useState(location.state?.sessionsPageSize || 20);
  const [sessionsTotalPages, setSessionsTotalPages] = useState(1);
  const [metricsSort, setMetricsSort] = useState(['created_at', 'desc']);
  const [sessionsSort, setSessionsSort] = useState(['event_time', 'desc']);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleSignOut = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/');
  };

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${config.apiUrl}/api/v5/metrics/user-export`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Accept': 'text/csv',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to export user metrics');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'user-metrics.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) {
          filename = match[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    setLoading(true);
    console.log('loading should be true')
    const fetchUserMetrics = async () => {
      const response = await fetch(`${config.apiUrl}/api/v5/metrics/user?page=${metricsPage}&page_size=${metricsPageSize}&sort_by=${metricsSort[0]}&order=${metricsSort[1]}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserMetrics(data.data.users); // Assuming the data is in the 'data' field
        setMetricsTotalPages(data.data.total_pages);
      } else {
        console.error('Failed to fetch user metrics');
      }
    };

    const fetchUserSessions = async () => {
      const response = await fetch(`${config.apiUrl}/api/v5/metrics/user-sessions?page=${sessionsPage}&page_size=${sessionsPageSize}&sort_by=${sessionsSort[0]}&order=${sessionsSort[1]}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserSessions(data.data.sessions); // Assuming the data is in the 'data' field
        setSessionsTotalPages(data.data.total_pages);
      } else {
        console.error('Failed to fetch user sessions');
      }
    };

    fetchUserMetrics();
    fetchUserSessions();
    setLoading(false);
  }, [metricsPage, metricsPageSize, sessionsPage, sessionsPageSize, metricsSort, sessionsSort]);

  return (
    <div>
      <Tabs actions={(
        <>
          <button className="download-button" onClick={handleDownload} disabled={isExporting}>
            {isExporting ? 'Preparing download...' : 'Download CSV'}
          </button>
          <button className="signout-button" onClick={handleSignOut}>
            Sign out
          </button>
        </>
      )}>
        <Tab label="User Metrics">
          {userMetrics &&
            <><Table
            data={userMetrics}
            setSort={setMetricsSort}
            columns={[
              'user_id',
              'email',
              'display_name',
              'last_login_time',
              'login_count',
              'first_name',
              'last_name',
              'institution',
              'professional_title',
              'created_at',
              'updated_at',
            ]}
            sortColumn={metricsSort[0]}
            sortDirection={metricsSort[1]}
            loading={loading}
          />
          <Pagination
            currentPage={metricsPage}
            totalPages={metricsTotalPages}
            onPageChange={setMetricsPage}
            pageSize={metricsPageSize}
            onPageSizeChange={setMetricsPageSize}
          /></>
          }
        </Tab>
        <Tab label="User Sessions">
          {
            userSessions && 
            (<><Table
              data={userSessions}
              setSort={setSessionsSort}
              columns={[
                'id',
                'user_id',
                'event_time',
                'client_version',
                'ip_address',
                'user_agent',
                'display_name',
                'email',
              ]}
              sortColumn={sessionsSort[0]}
              sortDirection={sessionsSort[1]}
              loading={loading} /><Pagination
                currentPage={sessionsPage}
                totalPages={sessionsTotalPages}
                onPageChange={setSessionsPage}
                pageSize={sessionsPageSize}
                onPageSizeChange={setSessionsPageSize} /></>)
          }
        </Tab>
      </Tabs>
    </div>
  );
}

export default Hello;
